import { FC, Dispatch, SetStateAction } from 'react';
import { Grid, MenuItem } from '@mui/material';
import { SMBSelect } from '../../../../components';
import { IDropdownResponse, IMaterialClasses } from '../../../../models';
import { useQuery } from 'react-query';
import { getMaterialClassChildren } from '../../../../fetch';

interface IManageMaterialClassDropdown {
  materialClassId: string | null;
  depth: number;
  setSelectedMaterialClassIdFilter: Dispatch<SetStateAction<string | null>>;
  hasAppliedFilters?: boolean;
  width?: number;
  required?: boolean;
  selectedValues?: IMaterialClasses[];
  setSelectedValues?: Dispatch<SetStateAction<IMaterialClasses[] | []>>;
  setSelectionsCleared: (val: boolean) => void;
}

export const ManageMaterialClassDropdown: FC<IManageMaterialClassDropdown> = ({
  materialClassId,
  depth,
  setSelectedMaterialClassIdFilter,
  width,
  required,
  selectedValues,
  setSelectedValues,
  setSelectionsCleared
}) => {
  const { isLoading: isLoadingDropdown, data: materialClassChildren } = useQuery<
    IDropdownResponse[],
    Error
  >(
    ['material-class-children', materialClassId],
    async () => await getMaterialClassChildren(materialClassId)
  );

  const handleDropdownChange = (value: string) => {
    setSelectedValues(prevValues => {
      const newValue: IMaterialClasses = {
        value,
        description: materialClassChildren?.find(item => item.value === value)?.description || '',
        shorthand: null
      };
      const updatedValues = [...prevValues.slice(0, depth), newValue];

      return updatedValues;
    });

    setSelectionsCleared(true);
    setSelectedMaterialClassIdFilter(value);
  };

  return materialClassChildren?.length > 0 ? (
    <>
      <Grid item xs={12} sm={width || 4}>
        <SMBSelect
          required={required && depth === 0 ? true : false}
          onChange={e => handleDropdownChange(e.target.value)}
          handleClear={() => {
            setSelectedValues(prevValues => prevValues.slice(0, depth));
            setSelectedMaterialClassIdFilter(materialClassId);
          }}
          variant="outlined"
          name={`selectedMaterialClassId-${depth ?? 0}`}
          label={depth ? `Sub Category` : `Material Class`}
          id={`selectedMaterialClassId-${depth ?? 0}`}
          value={selectedValues[depth]?.value || ''}
          disabled={isLoadingDropdown}
          InputLabelProps={{ shrink: !!selectedValues[depth]?.value }}
        >
          {materialClassChildren?.map((materialClass: IDropdownResponse, index) => (
            <MenuItem key={index} value={materialClass.value}>
              {materialClass.description}
            </MenuItem>
          ))}
        </SMBSelect>
      </Grid>

      {selectedValues[depth]?.value && (
        <ManageMaterialClassDropdown
          materialClassId={selectedValues[depth].value}
          depth={depth + 1}
          setSelectedMaterialClassIdFilter={setSelectedMaterialClassIdFilter}
          width={width || 4}
          required={required}
          selectedValues={selectedValues}
          setSelectedValues={setSelectedValues}
          setSelectionsCleared={setSelectionsCleared}
        />
      )}
    </>
  ) : null;
};
